import React from "react";

import { DateTime } from "luxon";

import { V2 } from "../../Api";
import { formatDateLocalMonth, formatDateLocalMonthShort, formatDateLocalNumeric, formatName, formatTimeLocal } from "../../Utilities";

import "../../surgerylog.css";

interface ISurgeryBookViewState {
    error?: string;
    hasProcessed: boolean;

    operations?: any[];
    pageOperations: any[][];

    endDate: DateTime;
    startDate: DateTime;

    yearCount: number;
    monthCountOffset: number;

    location: string;
    reportType: string;
    person: string;
    personType: string;
    visitType: string;

    hidePatientName: boolean;
}

class SurgeryBookView extends React.Component<{}, ISurgeryBookViewState> {
    private bigTableRef: React.RefObject<HTMLTableElement>;

    constructor(props: Readonly<{}>) {
        super(props);

        this.state = {
            hasProcessed: false,

            operations: [],
            pageOperations: [],

            endDate: DateTime.local(),
            startDate: DateTime.local(),

            yearCount: 0,
            monthCountOffset: 0,

            location: "",
            reportType: "!office",
            person: "",
            personType: "all",
            visitType: "",

            hidePatientName: false,
        };

        this.bigTableRef = React.createRef();

        this.handleMessage = this.handleMessage.bind(this);
        this.loadReport = this.loadReport.bind(this);
    }

    public componentDidMount() {
        // Request report data from main window
        window.addEventListener("message", this.handleMessage, false);
        window.opener.postMessage({ action: "report_request" }, "*");
    }

    public componentWillUnmount() {
        window.removeEventListener("message", this.handleMessage);
    }

    public render() {
        if (this.state.error) {
            return <div className="error-box">{this.state.error}</div>;
        }

        return (
            <div className="page-flow-root page-size-ledger">
                <div className="print-page print-cover-test">
                    <h1>{formatDateLocalMonth(this.state.startDate)}</h1>
                </div>
                {
                    this.state.pageOperations && this.state.pageOperations.length > 0
                        ? this.state.pageOperations.map((p, pi) => (
                            <div key={pi} className="print-page">
                                {this.renderTable(p)}
                                <span className="page-number">{formatDateLocalMonthShort(this.state.startDate)} - {pi + 1}</span>
                            </div>
                        ))
                        : ""
                }
                {
                    this.state.operations
                        ? <div className="print-page">
                            {this.renderTable(this.state.operations, true)}
                        </div>
                        : ""
                }
            </div>
        );
    }

    private async loadReport(before: DateTime, after: DateTime, location: string, type: string, person?: string, personType?: string, visitType?: string) {
        try {
            if (type === "officeindigent_year") {
                type = "officeindigent";
                after = after.startOf("year");
                before = before.endOf("year").plus({ millisecond: 1 });
            }

            const parameters = [
                "mode=expanded",
                "status=complete",
                "limit=10000",
                `before=${before.toISO()}`,
                `after=${after.toISO()}`,
                `location=${location}`,
                `type=${type}`,
                `person=${person || ""}`,
                `person_type=${personType || "all"}`,
                `visit_type=${visitType || ""}`
            ];

            // Count between start of year and start of report
            const yearAfter = after.startOf("year");

            const countParameters = [
                "status=complete",
                `type=${type}`,
                `before=${after.toISO()}`, // Stop just before start of report
                `after=${yearAfter.toISO()}`, // Start of year
                `location=${location}`
            ];

            const [items, count] = await Promise.all([
                V2.get<any[]>(`surgery/summary?${parameters.join("&")}`),
                V2.get<any>(`surgery/count?${countParameters.join("&")}`)
            ]);

            this.setState({
                operations: items, // .filter(a => a.appointmentType !== 1 /* Non-indigent office visit */),
                pageOperations: [],

                yearCount: count,
                monthCountOffset: (after.hasSame(DateTime.fromISO("2019-03-01"), "month") ? 96 : 0) // WCAS March 2019 Kludge
            }, () => this.processTable());
        }
        catch (e) {
            this.setState({
                error: e.message
            });
        }
    }

    private renderNameList(names: string[]) {
        return names.length === 0 ? "" : names.reduce((p, v) => `${p}, ${v}`)
    }

    private renderSurgeryList(surgery: any) {
        let items = [surgery.primarySurgery.shortName];
        items = items.concat(surgery.primarySurgeryOptions.map((b: any) => (b.glossaryOption && (b.glossaryOption.shortName || b.glossaryOption.fullName)) || b.id));
        if (surgery.additionalSurgeries && surgery.additionalSurgeries.length > 0) {
            for (const item of surgery.additionalSurgeries) {
                items.push(item.surgery.shortName);
                for (const option of item.options) {
                    items.push((option.glossaryOption && (option.glossaryOption.shortName || option.glossaryOption.fullName)) || option.id)
                }
            }
        }

        return items.join(", ");
    }

    private specimenToString(specimen: any) {
        if (specimen.size) {
            return `${specimen.name} (${specimen.size})`;
        }
        else if (specimen.location) {
            return `${specimen.name} (${specimen.location})`;
        }
        else if (specimen.size && specimen.location) {
            return `${specimen.name} (${specimen.size}, ${specimen.location})`;
        }
        else {
            return specimen.name;
        }
    }

    private raceToString(race: number) {
        switch (race) {
            case 0: return "?"; // "Unknown";
            case 1: return "X"; // "Declined";
            case 2: return "O"; // "Other";
            case 100: return "W"; // "White";
            case 200: return "B"; // "Black / African American";
            case 300: return "I"; // "American Indian / Alaskan Native";
            case 400: return "A"; // "Asian";
            case 500: return "P"; // "Native Hawaiian / Pacific Islander";
            case 600: return "H"; // "Hispanic / Latino";
            default: return `? ${race}`;
        }
    }

    private handleMessage(e: MessageEvent) {
        if (e.data.action && e.data.action === "report_parameters") {
            // Receiving report parameters
            this.setState({
                endDate: DateTime.fromISO(e.data.before),
                startDate: DateTime.fromISO(e.data.after),

                location: e.data.location,
                reportType: e.data.reportType || "!office",
                person: e.data.person,
                personType: e.data.personType,
                visitType: e.data.visitType,

                hidePatientName: !!e.data.hidePatientName,
            }, () => this.loadReport(this.state.endDate, this.state.startDate, this.state.location, this.state.reportType, this.state.person, this.state.personType, this.state.visitType));
        }
    }

    private processTable() {
        if (!this.bigTableRef.current || !this.state.operations) {
            return;
        }

        const bodyElement = this.bigTableRef.current.querySelector("tbody");
        const headerElement = this.bigTableRef.current.querySelector("thead");
        const pageElement: HTMLDivElement | null = document.querySelector(".print-page");
        if (!bodyElement || !headerElement || !pageElement) {
            return;
        }

        // Page height - table header - page footer - 24px kludge
        const pageHeight = pageElement.offsetHeight - headerElement.offsetHeight - (0.25 * 96) - 24;
        let heightTotal = 0;
        let rowCount = 0;
        let totalRowCount = 0;
        const pages: any[] = [];
        const operations = this.state.operations;

        for (let i = 0; i < bodyElement.children.length; i++) {
            const child = bodyElement.children.item(i)!;

            const height = child.scrollHeight + 1;

            if (heightTotal + height >= pageHeight) {
                // Page break
                pages.push(operations.splice(0, rowCount));
                heightTotal = 0;
                rowCount = 0;
            }

            heightTotal += height;
            rowCount++;
            totalRowCount++;

            operations[rowCount - 1].row = totalRowCount;
        }

        // Final page
        if (operations.length > 0) {
            pages.push(operations.splice(0));
        }

        this.setState(s => ({
            hasProcessed: true,

            operations: undefined,
            pageOperations: pages
        }));
    }

    private renderTable(operations: any[], isMainTable = false) {
        return <table className="table-print table-denser surgery-log-table nohover" ref={isMainTable ? this.bigTableRef : undefined}>
            <thead>
                <tr>
                    <th className="sl-col-number">YTD</th>
                    <th className="sl-col-number">MTD</th>
                    <th className="sl-col-date">Date</th>
                    <th className="sl-col-chart">Chart #</th>
                    <th className="sl-col-race">Race</th>
                    <th className="sl-col-name">Patient Name</th>
                    <th className="sl-col-dob">DoB</th>
                    <th className="sl-col-pri-surgeon">Surgeon</th>
                    <th className="sl-col-assist-surgeon">Assistant</th>
                    <th className="sl-col-anesthesia-providers">Anesthetist</th>
                    <th className="sl-col-anesthesia-type">Anesthesia</th>
                    <th className="sl-col-scrub">Scrub Tech</th>
                    <th className="sl-col-circulating">Circulating Nurse</th>
                    <th className="col-smallest col-center col-binary sl-col-count">Ct.<br /><small>Spg</small></th>
                    <th className="col-smallest col-center col-binary sl-col-count">Ct.<br /><small>Inst</small></th>
                    <th className="col-smallest col-center col-binary sl-col-count">Ct.<br /><small>Ndl</small></th>

                    <th className="sl-col-diagnosis">Diagnosis</th>
                    <th className="sl-col-operations">Procedures</th>
                    <th className="sl-col-time">Anesthesia Time</th>
                    <th className="sl-col-time">Operation Time</th>
                    <th className="sl-col-specimens">Specimens</th>
                    <th className="sl-col-complications">Complications</th>
                    <th className="sl-col-condition">Postop<br />Condition</th>
                </tr>
            </thead>
            <tbody>
                {
                    operations.map((a, i) => {
                        return (
                            <tr key={i}>
                                <td className="sl-col-number">{(a.row || 0) + this.state.yearCount}</td>
                                <td className="sl-col-number">{(a.row || 0) + this.state.monthCountOffset}</td>
                                <td className="sl-col-date">{formatDateLocalNumeric(a.date)}</td>
                                <td className="sl-col-chart">{a.patient.chartNumber}</td>
                                <td className="wrap sl-col-race">{this.raceToString(a.patient.race)}</td>
                                {this.state.hidePatientName ? <td className="wrap sl-col-name"><b>[REDACTED]</b></td> : <td className="wrap sl-col-name">{a.appointmentType === 2 ? <strong>(I)&nbsp;</strong> : ""}{a.patient.name.first}<br />{a.patient.name.last}</td>}
                                <td className="sl-col-dob">{formatDateLocalNumeric(a.patient.birthDate)}</td>
                                <td className="sl-col-pri-surgeon">{formatName(a.primarySurgeon.name)}</td>
                                <td className="wrap sl-col-assist-surgeon">{this.renderNameList(a.assistingSurgeons.map((v: any) => formatName(v.name, true)))}</td>
                                <td className="wrap sl-col-anesthesia-providers">{this.renderNameList(a.anesthesiaProviders.map((v: any) => formatName(v.name, true)))}</td>
                                <td className="sl-col-anesthesia-type">{a.anesthesiaType}</td>
                                <td className="wrap sl-col-scrub">{this.renderNameList(a.scrubNurses.map((v: any) => formatName(v.name, true)))}</td>
                                <td className="wrap sl-col-circulating">{this.renderNameList(a.circulatingNurses.map((v: any) => formatName(v.name, true)))}</td>
                                <td className="col-center sl-col-count">{!!a.countedSponges ? <i className="icon-check" /> : <span>&mdash;</span>}</td>
                                <td className="col-center sl-col-count">{!!a.countedInstruments ? <i className="icon-check" /> : <span>&mdash;</span>}</td>
                                <td className="col-center sl-col-count">{!!a.countedNeedles ? <i className="icon-check" /> : <span>&mdash;</span>}</td>
                                <td className="wrap sl-col-diagnosis">{a.diagnosisPre}</td>
                                <td className="wrap sl-col-operations">{this.renderSurgeryList(a)}</td>
                                {/*<td>
                                {a.additionalSurgeries.map((s: any) => s.surgery.shortName).reduce((p: string, v: string) => p ? `${p}, ${v}` : v, "")}
                            </td>*/}
                                <td className="sl-col-time">{formatTimeLocal(a.anesthesiaStartTime, true) || "N/A"} &mdash; {formatTimeLocal(a.anesthesiaEndTime, true) || "N/A"}</td>
                                <td className="sl-col-time">{formatTimeLocal(a.operationStartTime, true) || "N/A"} &mdash; {formatTimeLocal(a.operationEndTime, true) || "N/A"}</td>
                                <td className="wrap sl-col-specimens">{a.specimens && a.specimens.length > 0 ? this.renderNameList(a.specimens.map((v: any) => this.specimenToString(v))) : "None"}</td>
                                <td className="wrap sl-col-complications">{a.complications || "None"}</td>
                                <td className="wrap sl-col-condition">{a.conditionPost}</td>

                            </tr>
                        )
                    })
                }
            </tbody>
        </table>
    }
}

export default SurgeryBookView;
